// ARQUIVO 100% COMPLETO E CORRIGIDO
import React, { useState, useMemo, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import useData from '../hooks/useData';
import useToast from '../hooks/useToast';
import GlassCard from '../components/ui/Card';
import GlassButton from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import { SettingsIcon, ChevronLeftIcon, ChevronRightIcon, CloseIcon, AddIcon, ListIcon } from '../components/ui/Icons';
import {
    QuickDatePicker,
    CustomDragLayer,
    SchedulableTask,
    ViewMoreModal,
    MonthView,
    WeekView
} from '../components/calendar/CalendarComponents';

const DesignCalendar = ({ tasks, onTaskClick, onNewTaskClick }) => {
    // --- INÍCIO DA ALTERAÇÃO: Removido 'tasks' do useData, agora vem via props ---
    const { projects, handleUpdateTask } = useData();
    // --- FIM DA ALTERAÇÃO ---
    const { showToast, ToastComponent } = useToast();
    const [viewMode, setViewMode] = useState('month');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [isTaskPoolOpen, setIsTaskPoolOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [taskCapacityLimit, setTaskCapacityLimit] = useState(5);
    const [viewingMoreForDate, setViewingMoreForDate] = useState(null);
    const [schedulingTaskId, setSchedulingTaskId] = useState(null);
    
    useEffect(() => {
        const savedLimit = localStorage.getItem('vflow_calendar_task_limit');
        if (savedLimit) setTaskCapacityLimit(Number(savedLimit));
    }, []);

    useEffect(() => {
        localStorage.setItem('vflow_calendar_task_limit', taskCapacityLimit);
    }, [taskCapacityLimit]);

    // --- INÍCIO DA ALTERAÇÃO: A lógica agora usa as tarefas filtradas recebidas via props ---
    const unscheduledTasks = useMemo(() => tasks.filter(task => !task.deadline), [tasks]);
    
    const scheduledTasksByDate = useMemo(() => {
        const groupedTasks = {};
        tasks.forEach(task => {
            if (task.deadline) { // Não precisa mais checar 'isArchived', já vem filtrado
                const date = new Date(task.deadline);
                const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
                const dateKey = utcDate.toISOString().split('T')[0];
                if (!groupedTasks[dateKey]) { groupedTasks[dateKey] = []; }
                groupedTasks[dateKey].push(task);
            }
        });
        return groupedTasks;
    }, [tasks]);
    // --- FIM DA ALTERAÇÃO ---
    
    const handleScheduleTask = (newDate) => {
        if (!schedulingTaskId) return;
        const utcDate = new Date(Date.UTC(newDate.getFullYear(), newDate.getMonth(), newDate.getDate()));
        const deadline = utcDate.toISOString().split('T')[0];
        const task = tasks.find(t => t.id === schedulingTaskId);
        if (task) {
            handleUpdateTask(schedulingTaskId, { deadline }, `agendou a tarefa para ${deadline}.`);
            showToast(`'${task.title}' agendada!`);
        }
        setSchedulingTaskId(null);
    };

    const handleDropTaskOnCalendar = (taskId, newDate) => {
        const deadline = newDate.toISOString().split('T')[0];
        const task = tasks.find(t => t.id === taskId);
        if (task && task.deadline !== deadline) {
            const message = task.deadline ? 'reagendou a tarefa' : 'agendou a tarefa';
            handleUpdateTask(taskId, { deadline }, `${message} para ${deadline}.`);
            showToast(`'${task.title}' agendada!`);
        }
    };
    
    const handlePrev = () => setCurrentDate(prev => { const d = new Date(prev); if (viewMode === 'month') { d.setMonth(d.getMonth() - 1); } else { d.setDate(d.getDate() - 7); } return d; });
    const handleNext = () => setCurrentDate(prev => { const d = new Date(prev); if (viewMode === 'month') { d.setMonth(d.getMonth() + 1); } else { d.setDate(d.getDate() + 7); } return d; });
    const handleGoToToday = () => setCurrentDate(new Date());

    const tasksForModal = viewingMoreForDate ? scheduledTasksByDate[viewingMoreForDate.toISOString().split('T')[0]] || [] : [];
    const commonProps = { taskCapacityLimit, onViewMoreClick: d => setViewingMoreForDate(d), projects };

    return (
        <DndProvider backend={HTML5Backend}>
            <CustomDragLayer tasks={tasks} />
            {ToastComponent}
            <div className="flex flex-col w-full h-full p-4 relative overflow-hidden">
                <div className={`flex-grow flex flex-col transition-all duration-500 ease-in-out ${isTaskPoolOpen ? 'mr-80' : 'mr-0'}`}>
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-4">
                            <h2 className="text-3xl font-bold text-white">{currentDate.toLocaleDateString('pt-BR', {month: 'long', year: 'numeric'})}</h2>
                            <div className="flex items-center space-x-2">
                                <button onClick={handlePrev} className="p-2 rounded-full hover:bg-white/10"><ChevronLeftIcon /></button>
                                <GlassButton onClick={handleGoToToday} variant="minimal" className="!py-1 !px-3 !text-xs">Hoje</GlassButton>
                                <button onClick={handleNext} className="p-2 rounded-full hover:bg-white/10"><ChevronRightIcon /></button>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            <GlassButton onClick={onNewTaskClick} title="Nova Tarefa" className="!p-2"><AddIcon size={20} /></GlassButton>
                            <GlassButton onClick={() => setIsTaskPoolOpen(true)} title={`Tarefas não Agendadas (${unscheduledTasks.length})`} className="!p-2"><ListIcon size={20} /></GlassButton>
                            <div className="flex gap-1 rounded-lg bg-white/10 p-1">
                                <button onClick={() => setViewMode('month')} className={`px-3 py-1 text-sm rounded-md transition-colors ${viewMode === 'month' ? 'bg-indigo-500' : ''}`}>Mês</button>
                                <button onClick={() => setViewMode('week')} className={`px-3 py-1 text-sm rounded-md transition-colors ${viewMode === 'week' ? 'bg-indigo-500' : ''}`}>Semana</button>
                            </div>
                            <button onClick={() => setIsSettingsOpen(true)} className="p-2 rounded-full hover:bg-white/20"><SettingsIcon /></button>
                        </div>
                    </div>
                    <GlassCard className="flex-grow p-0 flex flex-col">
                        {viewMode === 'month' ? 
                            <MonthView currentDate={currentDate} scheduledTasksByDate={scheduledTasksByDate} commonProps={commonProps} /> : 
                            <WeekView currentDate={currentDate} scheduledTasksByDate={scheduledTasksByDate} onTaskClick={onTaskClick} onDropTask={handleDropTaskOnCalendar} taskCapacityLimit={taskCapacityLimit} projects={projects} />
                        }
                    </GlassCard>
                </div>
                
                <div className={`absolute top-0 right-0 h-full bg-gray-900/80 backdrop-blur-md shadow-2xl z-40 transition-transform duration-500 ease-in-out ${isTaskPoolOpen ? 'translate-x-0' : 'translate-x-full'} w-80 p-4`}>
                    <div className="flex justify-between items-center mb-4"><h3 className="text-xl font-bold text-white">Piscina de Tarefas</h3><button onClick={() => { setIsTaskPoolOpen(false); setSchedulingTaskId(null); }} className="p-2 rounded-full hover:bg-white/20"><CloseIcon /></button></div>
                    <div className="space-y-3 h-[calc(100%-50px)] overflow-y-auto pr-2">
                        {unscheduledTasks.length > 0 ? (
                            unscheduledTasks.map(task => (
                                <div key={task.id} className="relative">
                                    <SchedulableTask 
                                        task={task} 
                                        isSelected={schedulingTaskId === task.id}
                                        onScheduleClick={() => setSchedulingTaskId(schedulingTaskId === task.id ? null : task.id)}
                                    />
                                    {schedulingTaskId === task.id && (
                                        <QuickDatePicker 
                                            onSelectDate={handleScheduleTask} 
                                            onClose={() => setSchedulingTaskId(null)}
                                        />
                                    )}
                                </div>
                            ))
                        ) : (<p className="text-sm text-gray-400 text-center mt-8">Nenhuma tarefa para agendar.</p>)}
                    </div>
                </div>
            </div>
            <Modal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)}>
                <div className="p-6 text-white">
                    <h2 className="text-2xl font-bold">Configurações da Agenda</h2>
                    <div className="mt-6 space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Limite de tarefas por dia</label>
                            <input type="number" value={taskCapacityLimit} onChange={(e) => setTaskCapacityLimit(Number(e.target.value))} className="w-full p-2 bg-white/10 rounded-lg border border-white/30" />
                        </div>
                    </div>
                </div>
                <div className="p-4 bg-black/20 flex justify-end"><GlassButton onClick={() => setIsSettingsOpen(false)}>Fechar</GlassButton></div>
            </Modal>
            
            <ViewMoreModal date={viewingMoreForDate} tasks={tasksForModal} onClose={() => setViewingMoreForDate(null)} onTaskSelect={onTaskClick} projects={projects} />
        </DndProvider>
    );
};

export default DesignCalendar;